/**
 * Convert raw task analytics into a small, presentation-friendly descriptor for chips/tooltips.
 *
 * @param {TaskAnalytics|null|undefined} analytics
 * @returns {{ status: string, label: string, colorScheme: string, severity: 'good' | 'warning' | 'bad' | 'neutral', description?: string, score?: number }}
 */
export function getTaskHealthDisplay(analytics) {
  const clampRate = (val) => Math.max(0, Math.min(1, val));
  const completionRate = typeof analytics?.completionRate === 'number'
    ? clampRate(analytics.completionRate)
    : undefined;
  const ratePct = completionRate != null ? Math.round(completionRate * 100) : undefined;
  const firings = typeof analytics?.firingsCount === 'number' ? analytics.firingsCount : undefined;

  const healthState = (analytics?.healthState || '').toLowerCase();
  const baseScore = typeof analytics?.healthScore === 'number' ? analytics.healthScore / 100 : undefined;

  const pctLabel = ratePct != null ? `${ratePct}%` : '—';

  const healthMap = {
    healthy: { label: 'On track', colorScheme: 'green', severity: 'good', status: 'healthy' },
    slipping: { label: 'Slipping', colorScheme: 'yellow', severity: 'warning', status: 'slipping' },
    struggling: { label: 'Struggling', colorScheme: 'orange', severity: 'bad', status: 'struggling' },
    ignored: { label: 'Stalled', colorScheme: 'red', severity: 'bad', status: 'ignored' },
    unknown: { label: 'Learning', colorScheme: 'gray', severity: 'neutral', status: 'no_data' },
  };

  const copyForStatus = {
    healthy: ratePct != null
      ? `You're completing ${pctLabel} of these on time.`
      : 'Steady on-time completions.',
    slipping: ratePct != null
      ? `Completion dipped to ${pctLabel} recently. Finish a few on time to stay steady.`
      : 'Recent completions are slowing down. A few on-time finishes will help.',
    struggling: ratePct != null
      ? `Only ${pctLabel} completed on time. Push to finish this on time to get on track.`
      : 'Reminders are often missed. Completing the next few will help Ekkos adjust.',
    ignored: ratePct != null
      ? `Very few completions (${pctLabel}). Complete or pause if you need a break.`
      : 'Reminders are being skipped. Complete one or pause this task to reset.',
    no_data: 'Ekkos is figuring out your completion pattern for this task.',
    pending: 'Saving — analytics will appear after the first reminder fires.',
  };

  const mapped = healthMap[healthState] ?? healthMap.unknown;
  const statusKey = mapped.status || 'no_data';
  const description = copyForStatus[statusKey] ?? copyForStatus.no_data;

  // When we have some recent runs but no completion rate, hint about volume.
  const volumeHint = (ratePct == null && firings && firings > 0)
    ? `Seen ${firings} recent reminders — complete a few to unlock stats.`
    : '';

  return {
    status: mapped.status,
    label: mapped.label,
    colorScheme: mapped.colorScheme,
    severity: mapped.severity,
    description: [description, volumeHint].filter(Boolean).join(' '),
    microcopy: description,
    score: baseScore,
    completionRate: completionRate,
    completionRatePct: ratePct,
  };
}
